import { z } from "zod";
import { AccountIssueStatus } from "./account-issue";

export const inventoryAccountStatusSchema = z.enum([
  "active",
  "restricted",
  "needs_reset",
  "closed",
  // Rule-driven, system-owned statuses (not manually selectable):
  "on_hold",
  "written_off",
]);
export type InventoryAccountStatus = z.infer<
  typeof inventoryAccountStatusSchema
>;

/**
 * Admin-controlled "base" account status. The effective `accountStatus` is derived
 * from this base plus rule-driven overlays (`on_hold` / `written_off`) and the
 * credential-derived `needs_reset`. Storing the manual intent separately lets a
 * sticky `restricted` survive an overlay and be restored once the overlay clears.
 */
export const manualAccountStatusSchema = z.enum([
  "active",
  "restricted",
  "closed",
]);
export type ManualAccountStatus = z.infer<typeof manualAccountStatusSchema>;

export const inventoryTwoFactorStatusSchema = z.enum([
  "none",
  "email_forwarding",
  "otp_login",
  "otp_booking",
  "otp_authenticator",
  "ah_to_book",
  "no_need_to_check",
  "agent",
  // Legacy values kept for backward compatibility
  "enabled",
  "disabled",
  "unknown",
]);
export type InventoryTwoFactorStatus = z.infer<
  typeof inventoryTwoFactorStatusSchema
>;

/**
 * Account Value (USD, = available miles × buy CPM) below which an account is
 * written off. Constant for now; may move to an editable setting later.
 */
export const WRITE_OFF_THRESHOLD_USD = 100;

export interface DeriveAccountStatusInput {
  /** Admin-controlled base status (active / restricted / closed). */
  manualAccountStatus: ManualAccountStatus;
  /** Account issue status, if any. */
  problemStatus?: AccountIssueStatus;
  /** Account value in USD (available miles × buy CPM). */
  accountValue: number;
  /**
   * Remaining available miles. A depleted account (0 available) is never written
   * off on value — its $0 value is depletion, not a loss — so it falls through to
   * its account-health status while the lifecycle `status` field carries `depleted`.
   */
  availableMiles: number;
  /** Derived buy CPM (dollars/mile), or null when no cost basis is known. */
  buyCpm: number | null;
  /** Whether the account has both email and password on file. */
  hasCredentials: boolean;
}

/**
 * Computes the effective account status from its inputs using a fixed priority
 * chain (highest → lowest):
 *
 *   closed (terminal) → written_off → on_hold → restricted → needs_reset → active
 *
 * - `closed` is manual and terminal; it never auto-recovers.
 * - `written_off` fires on suspension, or when a known cost basis puts a NON-EMPTY
 *   account's value below the threshold. Two guards protect the value rule: the
 *   cost-basis guard (`buyCpm != null`) excludes brand-new accounts with no purchase
 *   history (value $0), and the depletion guard (`availableMiles > 0`) excludes
 *   sold-down accounts — a depleted account has $0 value but is `depleted`, not a
 *   loss, so it must not be written off.
 * - `on_hold` fires while the account issue status is `audited` or `issue`.
 * - `restricted` is manual and sticky; it survives an `on_hold` / `written_off`
 *   overlay and is restored when the overlay clears.
 * - `needs_reset` is credential-derived (missing email or password).
 * - `active` is the recovery default.
 */
export function deriveAccountStatus(
  input: DeriveAccountStatusInput,
): InventoryAccountStatus {
  const {
    manualAccountStatus,
    problemStatus,
    accountValue,
    availableMiles,
    buyCpm,
    hasCredentials,
  } = input;

  if (manualAccountStatus === "closed") {
    return "closed";
  }

  if (problemStatus === "suspended") {
    return "written_off";
  }

  if (
    buyCpm != null &&
    availableMiles > 0 &&
    accountValue < WRITE_OFF_THRESHOLD_USD
  ) {
    return "written_off";
  }

  if (problemStatus === "audited" || problemStatus === "issue") {
    return "on_hold";
  }

  if (manualAccountStatus === "restricted") {
    return "restricted";
  }

  if (!hasCredentials) {
    return "needs_reset";
  }

  return "active";
}

/**
 * Resolves the admin-controlled base status for an inventory.
 *
 * New (and already-healed) documents carry an explicit `manualAccountStatus`.
 * Legacy documents predate the field — and because backend reads go through the
 * generic repository WITHOUT zod parsing, the schema's `.default("active")` never
 * applies, so the field is genuinely `undefined` on those docs. We must NOT assume
 * `"active"` for them, or a write to a legacy `restricted`/`closed` account would
 * silently un-restrict/un-close it. Instead, recover the intent from the stored
 * effective `accountStatus` (the only manual values that ever existed there are
 * `restricted` and `closed`; everything else maps to `active`).
 *
 * Derived overlays (`on_hold` / `written_off`) carry no manual intent, so once an
 * account is in an overlay its base can no longer be recovered from `accountStatus`
 * — which is why the recompute persists the resolved base on first write, while the
 * pre-overlay status is still available.
 */
export function resolveManualAccountStatus(input: {
  manualAccountStatus?: ManualAccountStatus | null;
  accountStatus?: InventoryAccountStatus | null;
}): ManualAccountStatus {
  if (input.manualAccountStatus) {
    return input.manualAccountStatus;
  }
  if (input.accountStatus === "restricted") {
    return "restricted";
  }
  if (input.accountStatus === "closed") {
    return "closed";
  }
  return "active";
}

/** Accounts in these states can't be sold from; the server refuses sell orders. */
export function isInventoryAccountUnavailableForSale(
  accountStatus: InventoryAccountStatus | undefined,
): boolean {
  return (
    accountStatus === "restricted" ||
    accountStatus === "closed" ||
    accountStatus === "on_hold" ||
    accountStatus === "written_off"
  );
}
